import { useState, useEffect, useRef } from 'react'
import { Sparkles, Play } from 'lucide-react'
import type { Conversation, Message } from '@/types/conversations'

interface NextStepSuggestion {
  name: string
  prompt: string
  type?: 'test' | 'feature' | 'default'
}

interface NextStepsPanelProps {
  conversation: Conversation
  messages: Message[]
  onSuggestionClick: (prompt: string) => void
  processing: boolean
  suggestions?: NextStepSuggestion[]
}

export function NextStepsPanel({
  conversation,
  messages,
  onSuggestionClick,
  suggestions = []
}: NextStepsPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  // Reset hidden state when suggestions change (new task completed)
  const suggestionsKey = suggestions.map(s => s.prompt).join('|')
  useEffect(() => { setIsHidden(false) }, [suggestionsKey])

  // Collect suggestion prompts already visible in loaded turn_summary messages
  const visibleSuggestionPrompts = new Set<string>()
  for (const msg of messages) {
    if (msg.message_type === 'turn_summary' && msg.turn_data?.nextStepSuggestions) {
      for (const s of msg.turn_data.nextStepSuggestions) {
        visibleSuggestionPrompts.add(s.prompt)
      }
    }
  }

  // Filter out suggestions already shown in purple cards
  const filteredSuggestions = suggestions.filter(s => !visibleSuggestionPrompts.has(s.prompt))

  // Check for pending user actions
  const hasPendingPlan = messages.some(
    m => m.message_type === 'plan' && m.requires_approval &&
         !(m.conversation_id === conversation.id ? conversation.plan_approved : m.conversation_plan_approved)
  )
  const hasPendingQuestions = messages.some(
    m => m.message_type === 'questions' && !m.questions_answered && !m.questions_superseded
  )
  const hasPendingUserAction = hasPendingPlan || hasPendingQuestions

  const showPanel = filteredSuggestions.length > 0 &&
                    conversation.status === 'in_progress' &&
                    !hasPendingUserAction

  // Smooth auto-scroll using requestAnimationFrame (compositor-friendly, no stutter)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !showPanel) return

    let animId: number
    const speed = 0.3 // pixels per frame (~9px/s at 60fps)

    const step = () => {
      if (!isPaused && el.scrollLeft + el.clientWidth < el.scrollWidth - 1) {
        el.scrollLeft += speed
      }
      animId = requestAnimationFrame(step)
    }

    animId = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animId)
  }, [isPaused, showPanel, filteredSuggestions])

  if (!showPanel || isHidden) {
    return null
  }

  // Sort: feature suggestions first, test suggestions last
  const sorted = [...filteredSuggestions].sort((a, b) => {
    if (a.type === 'test' && b.type !== 'test') return 1
    if (a.type !== 'test' && b.type === 'test') return -1
    return 0
  })

  return (
    <div className="border-t backdrop-blur-xs bg-background/60 px-2 py-1.5 mx-2 rounded-t-xl flex-shrink-0">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsHidden(true)}
          className="flex items-center flex-shrink-0 p-0.5 rounded hover:bg-muted transition-colors"
          title="Hide suggestions"
        >
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        </button>

        <div
          ref={scrollRef}
          className="flex-1 min-w-0 overflow-x-auto scrollbar-hide"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="flex gap-1.5 w-max">
            {sorted.map((suggestion, index) => {
              const isTest = suggestion.type === 'test'
              return (
                <button
                  key={index}
                  onClick={() => onSuggestionClick(suggestion.prompt)}
                  className="flex-shrink-0 text-[11px] px-2.5 py-0.5 rounded-full border border-border text-foreground/80 hover:bg-accent transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  {isTest && <Play className="h-3 w-3" />}
                  {suggestion.name}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
